import React, { createContext, useContext, useState, useEffect } from "react";

export interface CartItem {
  id: number; // product_id
  productQuantityId: number; // product_quantity_id (để biết chọn quantity type nào)
  title: string;
  price: number;
  image: string;
  qty: number;
  unit: string;
  selected: boolean; // Trạng thái: có được tick chọn để thanh toán không
}

interface CartContextType {
  cartItems: CartItem[];
  addToCart: (item: Omit<CartItem, "qty" | "selected" | "productQuantityId">, qty?: number, productQuantityId?: number) => void;
  updateQty: (id: number, delta: number, productQuantityId?: number) => void;
  removeItem: (id: number, productQuantityId?: number) => void;
  toggleSelection: (id: number) => void;
  toggleAllSelection: (isSelected: boolean) => void;
  originalPrice?: number;
  clearSelectedItems: () => void;
  totalItems: number; // Tổng số lượng sản phẩm trong giỏ
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider = ({ children }: { children: React.ReactNode }) => {
  // Khởi tạo state từ localStorage (nếu có)
  const [cartItems, setCartItems] = useState<CartItem[]>(() => {
    const savedCart = localStorage.getItem("memys_cart");
    return savedCart ? JSON.parse(savedCart) : [];
  });
  const clearSelectedItems = () => {
    setCartItems((prev) => prev.filter((i) => !i.selected));
  };

  // Mỗi khi cartItems thay đổi, tự động lưu xuống localStorage
  useEffect(() => {
    localStorage.setItem("memys_cart", JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (item: Omit<CartItem, "qty" | "selected" | "productQuantityId">, qty: number = 1, productQuantityId: number = 0) => {
    setCartItems((prev) => {
      const existing = prev.find((i) => i.id === item.id && i.productQuantityId === productQuantityId);
      if (existing) {
        return prev.map((i) =>
            i.id === item.id && i.productQuantityId === productQuantityId ? { ...i, qty: i.qty + qty } : i
        );
      }
      return [...prev, { ...item, productQuantityId, qty, selected: true }]; // Mặc định tick chọn khi mới thêm
    });
  };

  const updateQty = (id: number, delta: number, productQuantityId?: number) => {
    setCartItems((prev) =>
        prev.map((i) => {
          if (i.id === id && (productQuantityId === undefined || i.productQuantityId === productQuantityId)) {
            const newQty = i.qty + delta;
            return newQty > 0 ? { ...i, qty: newQty } : i;
          }
          return i;
        })
    );
  };

  const removeItem = (id: number, productQuantityId?: number) => {
    setCartItems((prev) => prev.filter((i) => !(i.id === id && (productQuantityId === undefined || i.productQuantityId === productQuantityId))));
  };

  const toggleSelection = (id: number) => {
    setCartItems((prev) =>
        prev.map((i) => (i.id === id ? { ...i, selected: !i.selected } : i))
    );
  };

  const toggleAllSelection = (isSelected: boolean) => {
    setCartItems((prev) => prev.map((i) => ({ ...i, selected: isSelected })));
  };

  const totalItems = cartItems.length;

  return (
      <CartContext.Provider value={{
        cartItems, addToCart, updateQty, removeItem,
        toggleSelection, toggleAllSelection, totalItems, clearSelectedItems
      }}>
        {children}
      </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error("useCart must be used within CartProvider");
  return context;
};